import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface Indices {
  totalActiveTerritories: number;
  territoriesWithAds: number;
  totalAds: number;
  dryTerritories: number;
  indices: {
    coverageIndexPct: number;
    advertisementDensityIndex: number;
    visibilityIndexPct: number;
    mediumSaturationIndexPct: number;
    vendorPerformanceIndex: number;
    expansionOpportunityIndexPct: number;
  };
}
interface CoverageRow {
  geographyId: string;
  geographyName: string | null;
  adCount: number;
  distinctMediumCount: number;
  distinctVendorCount: number;
  lastActivityAt: string | null;
}
interface DryRow {
  geographyId: string;
  geographyName: string | null;
  adCount: number;
  distinctMediumCount: number;
  monthsSinceLastActivity: number | null;
  levels: number[];
}
interface DryResult {
  thresholds: { dryAreaMonths: number; coverageMinAds: number; neighbourFactor: number };
  levelDefinitions: Record<string, string>;
  summary: Record<string, number>;
  total: number;
  data: DryRow[];
}

/** §7 coverage analytics + §14 dry-area analytics + §18 indices. */
export function CoverageDryAreaScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay } = useBusinessDay();
  const [indices, setIndices] = useState<Indices | null>(null);
  const [coverage, setCoverage] = useState<CoverageRow[]>([]);
  const [dry, setDry] = useState<DryResult | null>(null);
  const [level, setLevel] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const q = level ? `?level=${level}` : '';
    Promise.all([
      apiRequest<{ data: Indices }>('/v1/geo-intel/coverage/indices', { token }),
      apiRequest<{ data: CoverageRow[] }>('/v1/geo-intel/coverage/territories', { token }),
      apiRequest<{ data: DryResult }>(`/v1/geo-intel/dry-areas/territories${q}`, { token }),
    ])
      .then(([i, c, d]) => {
        if (!alive) return;
        setIndices(i.data);
        setCoverage(c.data);
        setDry(d.data);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e instanceof ApiError && e.status === 403
          ? t('m16report.coverage.forbidden')
          : (e as Error).message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, level]);

  return (
    <main aria-label="coverage-dryarea">
      <h1>{t('m16report.coverage.title')}</h1>
      {error && <p role="alert" aria-label="coverage-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          {indices && (
            <section aria-label="geo-indices" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span aria-label="idx-coverage">{t('m16report.coverage.idx.coverage', { value: indices.indices.coverageIndexPct })}</span>
              <span aria-label="idx-density">{t('m16report.coverage.idx.density', { value: indices.indices.advertisementDensityIndex })}</span>
              <span aria-label="idx-visibility">{t('m16report.coverage.idx.visibility', { value: indices.indices.visibilityIndexPct })}</span>
              <span aria-label="idx-saturation">{t('m16report.coverage.idx.saturation', { value: indices.indices.mediumSaturationIndexPct })}</span>
              <span aria-label="idx-vendor">{t('m16report.coverage.idx.vendor', { value: indices.indices.vendorPerformanceIndex })}</span>
              <span aria-label="idx-expansion">{t('m16report.coverage.idx.expansion', { value: indices.indices.expansionOpportunityIndexPct })}</span>
            </section>
          )}

          <section aria-label="coverage-territories">
            <h2>{t('m16report.coverage.terr.title')}</h2>
            <DataTable
              ariaLabel="coverage-table"
              emptyMessage={t('m16report.coverage.empty')}
              rowKey={(c) => c.geographyId}
              rows={coverage}
              columns={[
                { key: 'territory', header: t('m16report.col.territory'), render: (c) => c.geographyName ?? '—' },
                { key: 'ads', header: t('m16report.col.ads'), render: (c) => c.adCount },
                { key: 'mediums', header: t('m16report.col.mediums'), render: (c) => c.distinctMediumCount },
                { key: 'vendors', header: t('m16report.col.vendors'), render: (c) => c.distinctVendorCount },
                { key: 'lastActivity', header: t('m16report.col.lastActivity'), render: (c) => (c.lastActivityAt ? atDay(c.lastActivityAt) : '—') },
              ]}
            />
          </section>

          <section aria-label="dry-areas">
            <h2>{t('m16report.dry.title')}</h2>
            <label>{t('m16report.dry.level')}
              <select aria-label="dry-level" value={level} onChange={(e) => setLevel(e.target.value)}>
                <option value="">{t('m16report.dry.level.all')}</option>
                <option value="1">{t('m16report.dry.level.1')}</option>
                <option value="2">{t('m16report.dry.level.2')}</option>
                <option value="3">{t('m16report.dry.level.3')}</option>
                <option value="4">{t('m16report.dry.level.4')}</option>
                <option value="5">{t('m16report.dry.level.5')}</option>
              </select>
            </label>
            {dry && (
              <>
                <p aria-label="dry-summary">
                  {t('m16report.dry.summary', { dry: dry.summary.dry, l1: dry.summary['1'], l2: dry.summary['2'], l3: dry.summary['3'], l4: dry.summary['4'], l5: dry.summary['5'] })}
                </p>
                <DataTable
                  ariaLabel="dry-table"
                  emptyMessage={t('m16report.dry.empty')}
                  rowKey={(d) => d.geographyId}
                  rows={dry.data}
                  columns={[
                    { key: 'territory', header: t('m16report.col.territory'), render: (d) => d.geographyName ?? '—' },
                    { key: 'ads', header: t('m16report.col.ads'), render: (d) => d.adCount },
                    { key: 'mediums', header: t('m16report.col.mediums'), render: (d) => d.distinctMediumCount },
                    { key: 'monthsIdle', header: t('m16report.dry.col.monthsIdle'), render: (d) => d.monthsSinceLastActivity ?? '—' },
                    { key: 'levels', header: t('m16report.dry.col.levels'), render: (d) => d.levels.join(', ') },
                  ]}
                />
              </>
            )}
          </section>
        </>
      )}
    </main>
  );
}
